'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext, useEffect } from 'react';
import { DivaEyeglassesData } from '../types';
import DivaBanner from './DivaBanner';
import DivaLikeDiamond from './DivaLikeDiamond';
import DivaPhotoWall from './DivaPhotoWall';
import DivaRadiant1 from './DivaRadiant1';
import DivaRadiant2 from './DivaRadiant2';
import DivaSparkling from './DivaSparkling';

interface DivaEyeglassesContentProps {
  pageData: DivaEyeglassesData;
  deviceType: EPlatform;
}

export default function DivaEyeglassesContent({
  pageData,
  deviceType,
}: DivaEyeglassesContentProps) {
  // App相关状态
  const { deviceType: contextDeviceType } = useContext(ResponsiveContext);
  const actualDeviceType = contextDeviceType || deviceType; // 优先使用context中的deviceType
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(actualDeviceType);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 在App内嵌环境时初始化通信
  useEffect(() => {
    if (inAppH5) {
      handleAppCommunication();
    }
  }, [inAppH5]);

  // 处理App通信逻辑
  const handleAppCommunication = async () => {
    try {
      // 注册token回调通信
      appCommunicationManager.registerCallback(
        EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
        async (_token) => {
          logger.log('App登录token:', _token);
          if (_token !== 'ClearToken') {
            setToken(_token);
            // 获取用户信息
            await refreshAccountInfo();
          } else {
            // 清空当前用户信息
            setToken('');
            setUserInfo({});
          }
        },
        {},
        'BackTokenInfo' as any, // 使用BackTokenInfo的postMessage方法
      );
    } catch (error) {
      logger.error('App通信初始化失败:', error);
    }
  };

  // 组件销毁时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  return (
    <div className="diva-eyeglasses-page bg-black font-normal text-[0px]">
      {/* Banner区域 - 传入完整的pageData和deviceType，包含所有优惠券功能 */}
      {(pageData as any).banner_image && (
        <DivaBanner pageData={pageData as any} deviceType={deviceType} />
      )}

      {/* 钻石商品展示 - 使用diamond数据，对应Vue版本的LikeDianond组件 */}
      {(pageData as any).diamond && (
        <DivaLikeDiamond pageData={pageData as any} deviceType={deviceType} />
      )}

      {/* 照片墙 - 使用photo_wall数据，对应Vue版本的PhotoWall组件 */}
      {(pageData as any).photo_wall && (
        <DivaPhotoWall pageData={pageData as any} deviceType={deviceType} />
      )}

      {/* Radiant1商品展示区域 - 使用radiant1数据，对应Vue版本的Radiant1组件（位于照片墙下方） */}
      {(pageData as any).radiant1 && (pageData as any).radiant1_title_image && (
        <DivaRadiant1 pageData={pageData as any} deviceType={deviceType} />
      )}

      {/* Radiant2双轮播展示区域 - 使用radiant2数据，对应Vue版本的Radiant2组件 */}
      {(pageData as any).radiant2 && (
        <DivaRadiant2 pageData={pageData as any} deviceType={deviceType} />
      )}

      {/* Sparkling商品展示 - 使用product_data数据，对应Vue版本的Sparkling组件 */}
      {(pageData as any).product_data?.list && (pageData as any).product_data.list.length > 0 && (
        <DivaSparkling pageData={pageData as any} deviceType={deviceType} />
      )}
    </div>
  );
}
